'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface CarImage {
  url: string;
  alt: string;
  sort_order: number;
}

interface Car {
  id: string;
  make: string;
  model: string;
  year: number;
  mileage: string;
  fuel: string;
  transmission: string;
  engine: string;
  description: string;
  price: string;
  badge?: string | null;
  img: string;
  alt: string;
  images: { src: string; alt: string }[];
}

// ─── Carousel ────────────────────────────────────────────────────────────────
function ImageCarousel({ images }: { images: Car['images'] }) {
  const [current, setCurrent] = useState(0);

  const prev = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  };

  const next = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/assets/images/no_image.png';
  };

  return (
    <div className="relative overflow-hidden bg-gray-100" style={{ height: '200px' }}>
      {images.map((img, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: i === current ? 1 : 0, pointerEvents: i === current ? 'auto' : 'none' }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading={i === 0 ? 'eager' : 'lazy'}
            onError={handleError}
          />
        </div>
      ))}

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Prethodna slika"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white rounded-full w-7 h-7 flex items-center justify-center transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            onClick={next}
            aria-label="Sljedeća slika"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white rounded-full w-7 h-7 flex items-center justify-center transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.preventDefault(); setCurrent(i); }}
                aria-label={`Slika ${i + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? 'bg-white scale-125' : 'bg-white/50'}`}
              />
            ))}
          </div>

          <span className="absolute top-2 right-2 z-10 bg-black/50 text-white text-xs px-2 py-0.5 rounded-sm">
            {current + 1}/{images.length}
          </span>
        </>
      )}
    </div>
  );
}

// ─── Grid ────────────────────────────────────────────────────────────────────
export default function CarsGrid() {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const supabase = createClient();

        const { data: carsData, error: carsError } = await supabase
          .from('cars')
          .select('*')
          .order('sort_order', { ascending: true });

        if (carsError) throw carsError;

        const { data: imagesData, error: imagesError } = await supabase
          .from('car_images')
          .select('car_id, url, alt, sort_order')
          .order('sort_order', { ascending: true });

        if (imagesError) throw imagesError;

        const imagesByCarId: Record<string, CarImage[]> = {};
        imagesData?.forEach((img) => {
          if (!imagesByCarId[img.car_id]) imagesByCarId[img.car_id] = [];
          imagesByCarId[img.car_id].push(img);
        });

        const mapped: Car[] = (carsData || []).map((row) => {
          const carImages = imagesByCarId[row.id] || [];
          let images: { src: string; alt: string }[] = carImages.map((ci) => ({
            src: ci.url,
            alt: ci.alt || `${row.make} ${row.model}`,
          }));
          // Fallback to legacy img column if no car_images rows
          if (images.length === 0 && row.img) {
            images = [{ src: row.img, alt: row.alt || `${row.make} ${row.model}` }];
          }
          if (images.length === 0) {
            images = [{ src: '/assets/images/no_image.png', alt: `${row.make} ${row.model}` }];
          }
          return {
            id: row.id,
            make: row.make,
            model: row.model,
            year: row.year,
            mileage: row.mileage,
            fuel: row.fuel,
            transmission: row.transmission,
            engine: row.engine,
            description: row.description,
            price: row.price,
            badge: row.badge,
            img: row.img,
            alt: row.alt,
            images,
          };
        });

        setCars(mapped);
      } catch (err: any) {
        setError('Greška pri učitavanju vozila.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  if (loading) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto py-20 text-center text-muted-foreground text-sm">{error}</div>
      </section>
    );
  }

  if (cars.length === 0) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto py-20 text-center text-muted-foreground text-sm">
          Trenutno nema dostupnih vozila. Provjerite uskoro.
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding px-6">
      <div className="max-w-7xl mx-auto">
        {/* Count */}
        <div className="flex items-center justify-between mb-10">
          <p className="text-muted-foreground text-sm">
            Prikazano <span className="text-foreground font-bold">{cars.length}</span> vozila
          </p>
          <a
            href="/#kontakt"
            className="text-primary text-sm font-bold uppercase tracking-wide hover:text-accent transition-colors flex items-center gap-2"
          >
            Upit za vozilo
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cars.map((car, i) => (
            <CarCard key={car.id} car={car} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────
function CarCard({ car, index }: { car: Car; index: number }) {
  return (
    <div className={`bg-card border border-border rounded-sm overflow-hidden card-lift group fade-up-init stagger-${Math.min(index + 1, 6)}`}>
      {/* Carousel */}
      <div className="relative">
        <ImageCarousel images={car.images} />
        {car.badge && (
          <span className="absolute top-3 left-3 z-20 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-sm">
            {car.badge}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-foreground font-bold text-lg leading-tight">
              {car.make} {car.model}
            </h3>
            <span className="text-muted-foreground text-sm">{car.year}</span>
          </div>
          {car.price && (
            <span className="text-primary font-bold text-sm whitespace-nowrap ml-2">{car.price}</span>
          )}
        </div>

        <p className="text-muted-foreground text-xs leading-relaxed mb-5 line-clamp-2">{car.description}</p>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[
            { label: 'Kilometraža', value: car.mileage },
            { label: 'Gorivo', value: car.fuel },
            { label: 'Mjenjač', value: car.transmission },
            { label: 'Motor', value: car.engine },
          ].map((spec) => (
            <div key={spec.label} className="bg-secondary/50 border border-border rounded-sm px-3 py-2">
              <span className="text-muted-foreground text-xs block leading-none mb-1">{spec.label}</span>
              <span className="text-foreground text-xs font-bold">{spec.value}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          href="/#kontakt"
          className="w-full flex items-center justify-center gap-2 border border-primary text-primary px-4 py-3 rounded-sm text-sm font-bold uppercase tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
        >
          Pošaljite upit
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14m-7-7 7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}